import { useEffect, useRef, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Printer, Share2, FileDown, Save, PenLine, ImagePlus } from "lucide-react";
import { ShareLinkDialog } from "./ShareLinkDialog";
import { ExecSummary } from "./ExecSummary";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  ComposedChart, Line,
} from "recharts";

const COND_COLORS = { 1: "#10b981", 2: "#84cc16", 3: "#f59e0b", 4: "#f97316", 5: "#e11d48" };
const HZ_COLORS = { "Immediate Attention": "#e11d48", "12 Month Plan": "#f59e0b", "24 Month Plan": "#2563eb", Monitor: "#94a3b8" };

function ragOf(cls) {
  if ((cls.immediate_count || 0) > 0) return "red";
  if ((cls.m12_count || 0) > 0) return "amber";
  return "green";
}
const RAG_STYLE = {
  red: { row: "bg-rose-50/60", dot: "bg-rose-500", badge: "bg-rose-100 text-rose-700", label: "Immediate" },
  amber: { row: "bg-amber-50/50", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-700", label: "12 Month" },
  green: { row: "", dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700", label: "Healthy" },
};

export function BoardReport() {
  const { client } = useCapital();
  const { role } = usePermissions();
  const canEdit = ["executive", "admin", "finance_admin"].includes((role || "").toLowerCase());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [commentary, setCommentary] = useState("");
  const [editingCommentary, setEditingCommentary] = useState(false);
  const [savingCommentary, setSavingCommentary] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    setLogoUrl(null);
    if (!client) return;
    let url;
    api.get("/capital/client-logo/image", { params: { client }, responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setLogoUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [client]);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("client", client);
    fd.append("file", f);
    try {
      await api.post("/capital/client-logo", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      const r = await api.get("/capital/client-logo/image", { params: { client }, responseType: "blob" });
      setLogoUrl(URL.createObjectURL(r.data));
      toast.success("Client logo saved — it now appears on the PDF cover too");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    }
    e.target.value = "";
  };

  const load = () => {
    if (!client) return;
    setLoading(true);
    api.get("/capital/board-report", { params: { client }, timeout: 60000 })
      .then((r) => {
        setData(r.data);
        setCommentary((prev) => (editingCommentary ? prev : r.data.commentary || ""));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [client]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("gbe:methodology-changed", h);
    return () => window.removeEventListener("gbe:methodology-changed", h);
    // eslint-disable-next-line
  }, [client]);

  const saveCommentary = async () => {
    setSavingCommentary(true);
    try {
      await api.put("/capital/board-commentary", { client, text: commentary });
      toast.success("Commentary saved — it will appear in the PDF too");
      setEditingCommentary(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save commentary");
    } finally { setSavingCommentary(false); }
  };

  if (loading || !data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const ov = data.overview;
  const imm = ov.horizons.find((h) => h.horizon === "Immediate Attention") || { count: 0, budget: 0 };
  const m12 = ov.horizons.find((h) => h.horizon === "12 Month Plan") || { count: 0, budget: 0 };
  const m24 = ov.horizons.find((h) => h.horizon === "24 Month Plan") || { count: 0, budget: 0 };
  const totalRenewal = imm.budget + m12.budget + m24.budget;
  const horizonChart = ov.horizons.map((h) => ({ name: h.horizon.replace(" Attention", "").replace(" Plan", ""), full: h.horizon, budget: h.budget, count: h.count }));
  const topSitesChart = (data.top_sites || []).slice(0, 8).map((s) => ({ name: (s.site || "—").slice(0, 22), budget: s.total_renewal_budget, count: s.asset_count }));
  const forecastChart = (data.forecast?.items || []).map((f) => ({ year: f.year, budget: f.budget, count: f.count }));

  return (
    <div id="board-report" className="bg-white border border-slate-200 rounded-lg p-8 print:border-0 print:shadow-none">
      <div className="flex items-start justify-between border-b border-slate-200 pb-6 mb-6">
        <div className="flex items-start gap-4">
          {logoUrl && (
            <img src={logoUrl} alt={`${client} logo`} data-testid="board-client-logo"
                 className="h-14 w-auto max-w-[140px] object-contain rounded border border-slate-100 bg-white p-1" />
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Asset Intelligence</div>
            <h2 className="font-display text-2xl font-semibold text-slate-900 mt-1">Board Summary — Renewal Assessment</h2>
            <p className="text-sm text-slate-500 mt-1">
              {ov.client} · Prepared by GBE Group
              {data.methodology === "custom" && <Badge className="ml-2 bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10px]">Custom ranking methodology</Badge>}
            </p>
            {canEdit && (
              <>
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                       ref={logoInputRef} onChange={uploadLogo} data-testid="board-logo-input" />
                <button onClick={() => logoInputRef.current?.click()} data-testid="board-logo-upload-btn"
                        className="print:hidden mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-gbe transition-colors">
                  <ImagePlus className="w-3 h-3" /> {logoUrl ? "Replace client logo" : "Add client logo (shows on PDF cover)"}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button data-testid={TID.capitalExportBoard} onClick={() => window.print()} variant="outline">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button
            data-testid="capital-download-pdf"
            onClick={async () => {
              try {
                toast("Generating branded PDF…");
                const { data: blob } = await api.get("/capital/board-report.pdf", { params: { client }, responseType: "blob", timeout: 60000 });
                const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `GBE_Board_Report_${(client || "report").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("PDF downloaded");
              } catch { toast.error("Could not generate PDF"); }
            }}
            variant="outline" className="ml-2 border-gbe text-gbe hover:bg-gbe/5"
          >
            <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
          </Button>
          {["executive", "admin", "finance_admin", "ops_mgmt"].includes((role || "").toLowerCase()) && (
            <Button data-testid={TID.capitalShareOpen} onClick={() => setShareOpen(true)} className="bg-gbe hover:bg-gbe-hover text-white ml-2">
              <Share2 className="w-4 h-4 mr-1.5" /> Share with client
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
        <SummaryStat label="Total Assets Reviewed" value={fmtNumber(ov.asset_count)} />
        <SummaryStat label="Portfolio Replacement Value" value={fmtCurrency(ov.total_value)} />
        <SummaryStat label="Avg Health Score" value={ov.avg_health == null ? "Insufficient data" : `${ov.avg_health.toFixed(1)} / 100`} />
        <SummaryStat label="Avg Data Confidence" value={`${ov.avg_confidence.toFixed(1)} / 100`} />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        <Horizon title="Beyond Design Life" tone="rose" count={imm.count} budget={imm.budget} />
        <Horizon title="12 Month Plan" tone="amber" count={m12.count} budget={m12.budget} />
        <Horizon title="24 Month Plan" tone="blue" count={m24.count} budget={m24.budget} />
      </div>

      <div className="mt-6 rounded-md border border-blue-200 bg-blue-50/40 p-4">
        <div className="text-[10px] uppercase tracking-widest text-gbe font-medium">Recommended annual capital allocation</div>
        <div className="mt-1 font-display text-3xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(imm.budget + m12.budget)}</div>
        <div className="text-xs text-slate-600 mt-1">
          Combining Beyond Design Life and 12 Month Plan. Total 24-month strategic outlay: <span className="font-medium text-slate-900">{fmtCurrency(totalRenewal)}</span>.
        </div>
      </div>

      <ExecSummary />

      {/* Executive commentary */}
      <div className="mt-6 rounded-md border border-slate-200 p-4" data-testid="board-commentary-block">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Executive commentary</div>
          {canEdit && !editingCommentary && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs ml-auto print:hidden"
                    onClick={() => setEditingCommentary(true)} data-testid="board-commentary-edit-btn">
              <PenLine className="w-3 h-3 mr-1" /> Edit
            </Button>
          )}
        </div>
        {editingCommentary ? (
          <div className="mt-2 print:hidden">
            <Textarea value={commentary} onChange={(e) => setCommentary(e.target.value)} rows={4}
                      placeholder="Add board-level narrative for this client — it saves per client and prints in the PDF."
                      data-testid="board-commentary-textarea" />
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="h-7 bg-gbe hover:bg-gbe-hover text-white" onClick={saveCommentary}
                      disabled={savingCommentary} data-testid="board-commentary-save-btn">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingCommentary(false)}>Cancel</Button>
            </div>
          </div>
        ) : commentary ? (
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{commentary}</p>
        ) : (
          <p className="text-xs text-slate-400 mt-2 italic">No commentary yet{canEdit ? " — click Edit to add board narrative (included in the PDF)." : "."}</p>
        )}
      </div>

      {/* Charts row 1 — horizons + condition mix */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="Renewal budget by horizon">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={horizonChart}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
              <Tooltip formatter={(v, n) => (n === "budget" ? fmtCurrency(v) : v)} />
              <Bar dataKey="budget" name="Budget">
                {horizonChart.map((h) => <Cell key={h.full} fill={HZ_COLORS[h.full] || "#94a3b8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Condition mix (1 = new · 5 = end of life)">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.condition_mix || []}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
              <XAxis dataKey="condition" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 9 }} width={40} />
              <Tooltip formatter={(v, n) => (n === "budget" ? fmtCurrency(v) : v)} />
              <Bar dataKey="count" name="Assets">
                {(data.condition_mix || []).map((c) => <Cell key={c.condition} fill={COND_COLORS[c.condition] || "#94a3b8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 — 10-year forecast + top sites */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="10-year renewal forecast">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={forecastChart}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
              <XAxis dataKey="year" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
              <Tooltip formatter={(v, n) => (n === "Budget" ? fmtCurrency(v) : v)} />
              <Bar dataKey="budget" name="Budget" fill="#0f4c75" />
              <Line type="monotone" dataKey="count" name="Assets" stroke="#f59e0b" strokeWidth={2} dot={false} yAxisId={0} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Top sites by renewal budget">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topSitesChart} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={140} />
              <Tooltip formatter={(v, n) => (n === "Budget" ? fmtCurrency(v) : v)} />
              <Bar dataKey="budget" name="Budget" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      {data.concentration?.commentary && (
        <p className="mt-3 text-xs text-slate-600 italic" data-testid="board-concentration-note">{data.concentration.commentary}</p>
      )}

      {/* Capital scenarios — live, reconciled to portfolio (brief §10) */}
      {(data.scenarios_live?.scenarios || []).length > 0 && (
        <div className="mt-8">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Capital scenarios</h3>
          <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden" data-testid="board-scenarios-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left font-medium px-4 py-2">Scenario</th>
                <th className="text-right font-medium px-4 py-2">Capital required</th>
                <th className="text-right font-medium px-4 py-2">Assets</th>
                <th className="text-right font-medium px-4 py-2">Priority risk addressed</th>
                <th className="text-right font-medium px-4 py-2">High-priority remaining</th>
                <th className="text-right font-medium px-4 py-2">Deferred exposure</th>
              </tr>
            </thead>
            <tbody>
              {data.scenarios_live.scenarios.map((s) => (
                <tr key={s.scenario} className="border-b border-slate-100">
                  <td className="px-4 py-2">
                    <div className="text-slate-800 font-medium">{s.scenario}</div>
                    <div className="text-[11px] text-slate-500">{s.description}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(s.capital_required)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtNumber(s.assets_included)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.priority_risk_addressed_pct}%</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtNumber(s.high_priority_remaining)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{fmtCurrency(s.deferred_exposure)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-1.5">
            Scenarios reconcile to this client's live renewal pool: {fmtNumber(data.scenarios_live.pool?.assets)} assets · {fmtCurrency(data.scenarios_live.pool?.value)}.
          </p>
        </div>
      )}

      {/* Asset classes RAG */}
      <div className="mt-8">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Asset classes — attention status</h3>
        <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden" data-testid="board-class-rag-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-left font-medium px-4 py-2">Asset class</th>
              <th className="text-right font-medium px-4 py-2">Assets</th>
              <th className="text-right font-medium px-4 py-2">Immediate</th>
              <th className="text-right font-medium px-4 py-2">12M</th>
              <th className="text-right font-medium px-4 py-2">24M</th>
              <th className="text-right font-medium px-4 py-2">Renewal budget</th>
            </tr>
          </thead>
          <tbody>
            {(data.asset_classes_all || []).map((g) => {
              const rag = ragOf(g);
              const st = RAG_STYLE[rag];
              return (
                <tr key={g.asset_group} className={`border-b border-slate-100 ${st.row}`}>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} />
                      <Badge className={`${st.badge} hover:bg-inherit text-[10px]`}>{st.label}</Badge>
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-800 font-medium">{g.asset_group || "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtNumber(g.asset_count)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${g.immediate_count ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{g.immediate_count || 0}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${g.m12_count ? "text-amber-700 font-medium" : "text-slate-400"}`}>{g.m12_count || 0}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{g.m24_count || 0}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(g.total_renewal_budget)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Priority assets — dynamic heading (brief §8) */}
      <div className="mt-8">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-3" data-testid="board-priority-heading">
          {data.priority_mode === "immediate" ? "Beyond Design Life Assets" : "Highest-Priority Renewal Candidates"}
        </h3>
        {data.priority_mode !== "immediate" && (
          <p className="text-xs text-slate-500 -mt-2 mb-3">No assets currently require immediate attention — showing the highest-ranked renewal candidates instead.</p>
        )}
        <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-medium px-3 py-2">#</th>
              <th className="text-left font-medium px-3 py-2">Asset</th>
              <th className="text-left font-medium px-3 py-2">Site</th>
              <th className="text-left font-medium px-3 py-2">Class</th>
              <th className="text-right font-medium px-3 py-2">Cond</th>
              <th className="text-right font-medium px-3 py-2">Risk</th>
              <th className="text-right font-medium px-3 py-2">Priority</th>
              <th className="text-left font-medium px-3 py-2">Timing</th>
              <th className="text-right font-medium px-3 py-2">Cost</th>
              <th className="text-left font-medium px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {data.top_immediate.map((a) => (
              <tr key={a.asset_id} className="border-b border-slate-100">
                <td className="px-3 py-2 text-slate-500 tabular-nums">{a.display_rank}</td>
                <td className="px-3 py-2 text-slate-800 max-w-[220px] truncate">{a.asset_name}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[150px] truncate">{a.site_name}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{a.asset_group}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.condition ?? "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.risk ?? "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{Math.round(a.v6_priority_score ?? 0)}</td>
                <td className="px-3 py-2 text-xs">{a.timing}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(a.cost)}</td>
                <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px]">{a.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assets per site */}
      <div className="mt-8">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Assets per site</h3>
        <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden" data-testid="board-sites-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-2">Site</th>
              <th className="text-right font-medium px-4 py-2">Assets</th>
              <th className="text-right font-medium px-4 py-2">Immediate</th>
              <th className="text-right font-medium px-4 py-2">Imm + 12M budget</th>
              <th className="text-right font-medium px-4 py-2">24M budget</th>
              <th className="text-right font-medium px-4 py-2">Replacement value</th>
            </tr>
          </thead>
          <tbody>
            {(data.sites_all || []).slice(0, 25).map((s) => (
              <tr key={s.site} className={`border-b border-slate-100 ${s.immediate_count ? "bg-rose-50/40" : ""}`}>
                <td className="px-4 py-2 text-slate-800 max-w-[280px] truncate">{s.site || "—"}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtNumber(s.asset_count)}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${s.immediate_count ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{s.immediate_count || 0}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtCurrency(s.immediate_12m_budget)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">{fmtCurrency(s.twenty_four_m_budget)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(s.total_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 border-t border-slate-200 pt-4 text-xs text-slate-500 space-y-1">
        <p>
          These are assets identified for renewal assessment, not final replacement approval. Data confidence banding is derived from V6 confidence
          (High=90, Medium=75, Low=60).
        </p>
        <p>
          Source: AroFlo Asset Intelligence
          {data.methodology === "custom" ? " · Ranking: GBE custom methodology (see Methodology tab)" : " · Ranking: Default values"}
          {" "}· Report generated by GBE Group Data Hub.
        </p>
      </div>

      <ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">{title}</div>
      <div className="h-56 min-h-[224px]">{children}</div>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function Horizon({ title, tone, count, budget }) {
  const t = tone === "rose" ? "border-rose-200 bg-rose-50/40" : tone === "amber" ? "border-amber-200 bg-amber-50/40" : "border-blue-200 bg-blue-50/40";
  const c = tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-gbe";
  return (
    <div className={`rounded-md border ${t} p-4`}>
      <div className={`text-[10px] uppercase tracking-widest font-medium ${c}`}>{title}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(budget)}</div>
      <div className="text-xs text-slate-500 mt-0.5">{count} assets</div>
    </div>
  );
}
